/**
 * @file Sole home of a payment's base-account rules: the route it stored, the controls
 * a surface renders, their defaults, the payload keys, and what a read-only view shows.
 */
import { PAYMENT_ROUTE, PaymentRouteId } from "../Charge/PaymentRoute";
import { BankAccountInterface } from "../BankAccount/BankAccountInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import { PaymentInterface } from "./PaymentInterface";

export function getPaymentRouteIdForPayment(
  payment: { baseAccountCompanyId?: number | null } | null,
  agencyId: number,
): PaymentRouteId {
  // A payment predating CASA-12281 stores no company, and one saved incomplete stores
  // none either; neither names an owner account, so both read as the agency route.
  if (!payment || payment.baseAccountCompanyId == null || payment.baseAccountCompanyId === agencyId) {
    return PAYMENT_ROUTE.AGENCY;
  }
  return PAYMENT_ROUTE.DIRECT_TRACKED;
}

export function getBaseBankAccountPresentation(
  params: {
    paymentRouteId: PaymentRouteId;
    agencyId: number;
    ownerCompanyId: number | null;
    agencyBankAccountCount: number;
  },
): { usesSelector: boolean; baseAccountCompanyId: number | null; baseAccountIsOwner: boolean } {
  // Ids are coerced here: `undefined` is dropped by `JSON.stringify`, which would send a
  // two-key trio the backend cannot complete.
  if (params.paymentRouteId === PAYMENT_ROUTE.DIRECT_TRACKED) {
    return { usesSelector: false, baseAccountCompanyId: params.ownerCompanyId ?? null, baseAccountIsOwner: true };
  }
  return { usesSelector: params.agencyBankAccountCount > 0, baseAccountCompanyId: params.agencyId ?? null, baseAccountIsOwner: false };
}

export function getBaseBankAccountDefaults(
  params: {
    paymentRouteId: PaymentRouteId;
    usesSelector: boolean;
    baseAccountCompany: CompanyInterface | null;
    transactionIban: string;
  },
): { baseBankAccount: string; baseBankAccountName: string } {
  if (params.usesSelector) {
    return { baseBankAccount: "", baseBankAccountName: "" };
  }
  if (params.paymentRouteId === PAYMENT_ROUTE.DIRECT_TRACKED) {
    return {
      baseBankAccount: params.baseAccountCompany?.mainBankAccountId || "",
      baseBankAccountName: params.baseAccountCompany?.mainBankAccountName || params.baseAccountCompany?.name || "",
    };
  }
  return {
    baseBankAccount: params.transactionIban,
    baseBankAccountName: params.baseAccountCompany?.name || "",
  };
}

export function getBaseBankAccountFields(
  params: {
    isBankType: boolean;
    presentation: { usesSelector: boolean; baseAccountCompanyId: number | null };
    selectedBankAccountId: number | null;
    baseBankAccount: string;
    baseBankAccountName: string;
  },
): object {
  if (!params.isBankType) {
    return { agencyBankAccount: null };
  }
  if (params.presentation.usesSelector && !params.selectedBankAccountId) {
    return { agencyBankAccount: null };
  }
  if (params.presentation.usesSelector) {
    return { baseBankAccountId: params.selectedBankAccountId, agencyBankAccount: params.selectedBankAccountId };
  }
  return {
    baseBankAccount: params.baseBankAccount,
    baseBankAccountName: params.baseBankAccountName,
    baseAccountCompanyId: params.presentation.baseAccountCompanyId,
  };
}

export function getPaymentBaseAccountDisplay(
  params: {
    payment: PaymentInterface | null;
    agencyBankAccounts: BankAccountInterface[];
    companies: CompanyInterface[];
    agencyId: number;
  },
): { accountId: string; name: string } {
  // IBAN and name travel from one source as a pair, so the stored IBAN alone decides
  // whether anything is looked up — no render mixes a stored name with a found IBAN.
  if (!!params.payment?.baseBankAccount) {
    return { accountId: params.payment.baseBankAccount, name: params.payment.baseBankAccountName || "" };
  }
  if (getPaymentRouteIdForPayment(params.payment, params.agencyId) === PAYMENT_ROUTE.AGENCY) {
    const bankAccount: BankAccountInterface = (params.agencyBankAccounts || [])
      .find((account: BankAccountInterface) => account?.id === params.payment?.agencyBankAccount);
    return { accountId: bankAccount?.accountId || "", name: bankAccount?.name || "" };
  }
  const company: CompanyInterface = (params.companies || [])
    .find((item: CompanyInterface) => item?.id === params.payment?.baseAccountCompanyId);
  return { accountId: company?.mainBankAccountId || "", name: company?.mainBankAccountName || "" };
}
